import React, { useState, useEffect } from 'react';
import { expertService } from '../../api';
import Header from '../Header/Header';
import Footer from '../footer/Footer';
import { 
  FaSearch, 
  FaUserTie, 
  FaMapMarkerAlt, 
  FaMoneyBillWave,
  FaEuroSign,
  FaArrowRight,
  FaArrowLeft,
  FaExclamationCircle,
  FaFilter,
  FaEye,
  FaCrown
} from 'react-icons/fa';
import './ListeExperts.css';
import { Link } from 'react-router-dom';

const ListeExperts = () => {
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    total: 0,
    per_page: 12
  });
  const [filters, setFilters] = useState({
    ville: '',
    tarif: '',
    services: ''
  });
  const [imageErrors, setImageErrors] = useState({});
  const [imageOrientations, setImageOrientations] = useState({});

  const fetchExperts = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: 12,
        search: searchTerm,
        ...filters
      };
      
      const response = await expertService.getAll(params);
      const expertsData = response.data.data || response.data || [];
      
      const expertsWithImages = expertsData.map(expert => {
        const expertWithImage = { ...expert };
        
        if (expert.image || expert.image_url) {
          const imagePath = expert.image || expert.image_url;
          expertWithImage.imageUrl = getImageUrl(imagePath);
        } else {
          expertWithImage.imageUrl = null;
        }
        
        // Ajouter un rating aléatoire pour l'affichage
        expertWithImage.rating = Math.floor(Math.random() * 3) + 3;
        
        return expertWithImage;
      });
      
      setExperts(expertsWithImages);
      setPagination({
        current_page: response.data.current_page || 1,
        total: response.data.total || 0,
        per_page: response.data.per_page || 12
      });
      
    } catch (err) {
      console.error('Erreur lors du chargement des experts:', err);
      setError(err.message || 'Erreur lors du chargement des experts');
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    const baseUrl = process.env.REACT_APP_API_URL || 'https://plateforme.etef-design.fr';
    
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    
    const cleanedPath = imagePath.startsWith('storage/') 
      ? imagePath.replace('storage/', '') 
      : imagePath;
    
    if (cleanedPath.includes('/')) {
      return `${baseUrl}/storage/${cleanedPath}`;
    } else {
      return `${baseUrl}/storage/annonces/${cleanedPath}`;
    }
  };

  const handleImageLoad = (expertId, event) => {
    const img = event.target;
    
    // Déterminer l'orientation de l'image
    const isPortrait = img.naturalHeight > img.naturalWidth;
    setImageOrientations(prev => ({
      ...prev,
      [expertId]: isPortrait ? 'portrait' : 'paysage'
    }));
  };

  const handleImageError = (expertId, imageUrl) => {
    setImageErrors(prev => ({ ...prev, [expertId]: true }));
  };

  const getDisplayImageUrl = (expert) => {
    if (imageErrors[expert.id]) {
      return null;
    }
    
    if (expert.imageUrl) {
      return expert.imageUrl;
    }
    
    if (expert.image || expert.image_url) {
      return getImageUrl(expert.image || expert.image_url);
    }
    
    return null;
  };

  useEffect(() => {
    fetchExperts();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchExperts(1);
  };

  const handlePageChange = (page) => {
    if (page > 0 && page <= Math.ceil(pagination.total / pagination.per_page)) {
      fetchExperts(page);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    fetchExperts(1);
  };

  const resetFilters = () => {
    setFilters({
      ville: '',
      tarif: '',
      services: ''
    });
    setSearchTerm('');
    fetchExperts(1);
  };

  const formatPrice = (price) => {
    if (!price) return 'Non spécifié';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const handleCardMouseMove = (e, cardId) => {
    const card = document.getElementById(`expert-card-${cardId}`);
    if (card) {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      card.style.setProperty('--mouse-x', `${x}px`);
      card.style.setProperty('--mouse-y', `${y}px`);
    }
  };

  if (loading) {
    return (
      <div className="experts-premium-loading">
        <div className="experts-premium-spinner"></div>
        <p style={{ marginTop: '20px', color: 'var(--expert-text-light)', fontSize: '1.1rem' }}>
          Chargement des experts...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="experts-premium-no-results">
        <div className="experts-premium-no-results-icon">
          <FaExclamationCircle />
        </div>
        <h3>Erreur de chargement</h3>
        <p>{error}</p>
        <button onClick={() => fetchExperts()} className="experts-premium-view-btn">
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="experts-premium-container">
      <div className="experts-background-animation"></div>
      <div className="experts-floating-elements">
        <div className="experts-floating-element"></div>
        <div className="experts-floating-element"></div>
        <div className="experts-floating-element"></div>
      </div>
      
      <Header />
      
      <div className="experts-container">
        <div className="experts-premium-header">
          <h1><FaUserTie /> Annuaire des experts</h1>
          <p className="experts-premium-subtitle">
            Découvrez notre sélection d'experts certifiés, prêts à vous accompagner 
            vers l'excellence
          </p>
        </div>

        <div className="experts-premium-search">
          <form onSubmit={handleSearch} className="experts-premium-search-form">
            <div className="experts-premium-search-input-container">
              <FaSearch className="experts-premium-search-icon" />
              <input
                type="text"
                placeholder="Rechercher un expert par nom, spécialité ou ville..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="experts-premium-search-input"
              />
            </div>
            <button type="submit" className="experts-premium-search-btn">
              <FaSearch /> Rechercher
            </button>
          </form>

          <div className="experts-premium-filters">
            <div className="experts-premium-filter-group">
              <select
                name="ville"
                value={filters.ville}
                onChange={handleFilterChange}
                className="experts-premium-filter-select"
              >
                <option value="">Toutes les villes</option>
                <option value="paris">Paris</option>
                <option value="lyon">Lyon</option>
                <option value="marseille">Marseille</option>
              </select>
            </div>
            <div className="experts-premium-filter-group">
              <select
                name="tarif"
                value={filters.tarif}
                onChange={handleFilterChange}
                className="experts-premium-filter-select"
              >
                <option value="">Tous les tarifs</option>
                <option value="gratuit">Gratuit</option>
                <option value="payant">Payant</option>
              </select>
            </div>
            <div className="experts-premium-filter-group">
              <input
                type="text"
                name="services"
                placeholder="Service recherché..."
                value={filters.services}
                onChange={handleFilterChange}
                className="experts-premium-filter-input"
              />
            </div>
            <div className="experts-premium-filter-actions">
              <button onClick={applyFilters} className="experts-premium-apply-btn">
                <FaFilter /> Appliquer
              </button>
              <button onClick={resetFilters} className="experts-premium-reset-btn">
                Réinitialiser
              </button>
            </div>
          </div>
        </div>

        {experts.length > 0 ? (
          <>
            <div className="experts-premium-grid">
              {experts.map((expert, index) => {
                const imageUrl = getDisplayImageUrl(expert);
                const orientation = imageOrientations[expert.id] || 'paysage';
                
                return (
                  <div 
                    key={expert.id}
                    id={`expert-card-${expert.id}`}
                    className="experts-premium-card"
                    style={{ animationDelay: `${index * 0.1}s` }}
                    onMouseMove={(e) => handleCardMouseMove(e, expert.id)}
                  >
                    <div className="experts-premium-status-badge">
                      <FaCrown /> Expert Certifié
                    </div>
                    
                    <div className={`experts-premium-card-image-container ${orientation}`}>
                      {imageUrl ? (
                        <img 
                          src={imageUrl} 
                          alt={`${expert.name} ${expert.prenom}`}
                          className={`experts-premium-img-complete ${orientation}`}
                          onLoad={(e) => handleImageLoad(expert.id, e)}
                          onError={() => handleImageError(expert.id, imageUrl)}
                          loading="lazy"
                        />
                      ) : (
                        <div className="experts-premium-no-img-complete">
                          <FaUserTie />
                        </div>
                      )}
                    </div>

                    <div className="experts-premium-card-content">
                      <h3 className="experts-premium-name">
                        {expert.name} {expert.prenom}
                      </h3>
                      
                      <div className="experts-premium-location">
                        <FaMapMarkerAlt />
                        <span>{expert.ville || 'Disponible partout en France'}</span>
                      </div>

                      <div className="experts-premium-pricing">
                        <span className="experts-premium-tarif">
                          <FaMoneyBillWave /> {expert.tarif || 'Consultation'}
                        </span>
                        {expert.prix && (
                          <span className="experts-premium-price">
                            <FaEuroSign /> {formatPrice(expert.prix)}
                          </span>
                        )}
                      </div>

                      <p className="experts-premium-bio">
                        {expert.bio ? (
                          expert.bio.length > 150 
                            ? `${expert.bio.substring(0, 150)}...`
                            : expert.bio
                        ) : (
                          "Expert passionné et engagé dans l'accompagnement vers l'excellence."
                        )}
                      </p>

                      <div className="experts-premium-card-footer">
                        <Link 
                          to={`/experts/${expert.id}`} 
                          className="experts-premium-view-btn"
                        >
                          <FaEye /> Découvrir le profil
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {pagination.total > pagination.per_page && (
              <div className="experts-premium-pagination">
                <div className="experts-premium-pagination-controls">
                  <button
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    disabled={pagination.current_page === 1}
                    className="experts-premium-pagination-btn"
                  >
                    <FaArrowLeft /> Précédent
                  </button>
                  
                  <div className="experts-premium-pagination-info">
                    Page {pagination.current_page} sur {Math.ceil(pagination.total / pagination.per_page)}
                    <span style={{ color: 'var(--expert-primary)', marginLeft: '5px' }}>
                      ({pagination.total} experts)
                    </span>
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    disabled={pagination.current_page * pagination.per_page >= pagination.total}
                    className="experts-premium-pagination-btn"
                  >
                    Suivant <FaArrowRight />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="experts-premium-no-results">
            <div className="experts-premium-no-results-icon">
              <FaUserTie />
            </div>
            <h3>Aucun expert trouvé</h3>
            <p>
              Aucun expert ne correspond à vos critères de recherche. 
              Essayez d'élargir vos filtres ou contactez-nous pour une recommandation personnalisée.
            </p>
            <button onClick={resetFilters} className="experts-premium-view-btn">
              Réinitialiser la recherche
            </button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ListeExperts;